import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { Close, ContentCopy, PersonAdd, Delete } from "@mui/icons-material";
import AppContext from "../../utils/app-context";
import { AuthContext } from "../../utils/auth-provider";
import { nanoid } from "nanoid";
import theme from "../../styles/theme";
import toast from "react-hot-toast";
import {
  lookupUserByEmail,
  fetchMemberProfiles,
  sendMemberInviteEmail,
  sendMemberAddedEmail,
} from "../../helpers/index";

export default function ManageMembersDialog({ open, onClose }) {
  const { state, dispatch } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [memberProfiles, setMemberProfiles] = useState({});
  const [inviteSent, setInviteSent] = useState(false);
  const [lastInvitedEmail, setLastInvitedEmail] = useState("");
  const [inviteError, setInviteError] = useState(null); // fallback link when email send fails

  const portal = state.app.portal;
  const members = portal?.members || [];
  const portalId = portal?.id;

  // Fetch Firestore profile data for accepted members on open
  useEffect(() => {
    if (!open || members.length === 0) return;
    fetchMemberProfiles(members, memberProfiles).then((profiles) => {
      if (Object.keys(profiles).length > 0) {
        setMemberProfiles((prev) => ({ ...prev, ...profiles }));
      }
    });
  }, [open, members]); // eslint-disable-line

  const handleAddMember = async () => {
    if (!email.trim()) return;

    const normalizedEmail = email.trim().toLowerCase();

    if (normalizedEmail === currentUser?.email?.toLowerCase()) {
      toast.error("You can't add yourself as a member.");
      return;
    }

    setLoading(true);
    setInviteSent(false);
    setInviteError(null);
    setLastInvitedEmail(normalizedEmail);

    try {
      const invite_code = nanoid(6);
      const found = await lookupUserByEmail(normalizedEmail);

      if (found) {
        // User exists — add immediately as accepted
        if (members.find((m) => m.uid === found.uid)) {
          toast.error("This user is already a member.");
          setLoading(false);
          return;
        }

        dispatch({
          type: "ADD_PROJECT_MEMBER",
          payload: {
            member: {
              uid: found.uid,
              role: "member",
              status: "accepted",
              invite_code,
              email: normalizedEmail,
              added_at: new Date().toISOString(),
            },
          },
        });

        setMemberProfiles((prev) => ({ ...prev, [found.uid]: found.data }));
        toast.success(`Member added (${normalizedEmail})!`);
        setEmail("");

        // Fire-and-forget: notify the user they've been added.
        // Errors are silently swallowed — the member already has access.
        sendMemberAddedEmail({
          email: normalizedEmail,
          portalId,
          projectName: portal?.title || portal?.name || "a project",
          inviterName: currentUser?.displayName || currentUser?.email || "A 10zebra creator",
        }).catch((err) => {
          console.warn("sendMemberAddedEmail: notification failed (non-blocking)", err);
        });
      } else {
        // No account — create pending invite and send invite email
        dispatch({
          type: "ADD_PROJECT_MEMBER",
          payload: {
            member: {
              uid: null,
              role: "member",
              status: "pending",
              invite_code,
              email: normalizedEmail,
              added_at: new Date().toISOString(),
            },
          },
        });

        setEmail("");

        try {
          await sendMemberInviteEmail({
            email: normalizedEmail,
            inviteCode: invite_code,
            portalId,
            projectName: portal?.title || portal?.name || "a project",
            inviterName: currentUser?.displayName || currentUser?.email || "A 10zebra creator",
          });
          setInviteSent(true);
          toast.success(`Invite email sent to ${normalizedEmail}!`);
        } catch (emailErr) {
          console.error("sendMemberInviteEmail failed:", emailErr);
          const fallbackLink = `${window.location.origin}/p/${portalId}?invite=${invite_code}`;
          setInviteError(fallbackLink);
          toast.error("Couldn't send invite email — copy the link below to share manually.");
        }
      }
    } catch (error) {
      console.error("handleAddMember error:", error);
      toast.error("Failed to add member. Please try again.");
    }

    setLoading(false);
  };

  const handleRemoveMember = (member) => {
    dispatch({
      type: "REMOVE_PROJECT_MEMBER",
      payload: { uid: member.uid, invite_code: member.invite_code },
    });
    toast.success("Member removed.");
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => toast.success("Invite link copied!"));
  };

  const getMemberEmail = (member) => {
    return member.email || (member.uid && memberProfiles[member.uid]?.email) || null;
  };

  const getMemberDisplayName = (member) => {
    if (member.uid && memberProfiles[member.uid]) {
      const p = memberProfiles[member.uid];
      return p.displayName || p.name || null;
    }
    return null;
  };

  const getMemberName = (member) => {
    const displayName = getMemberDisplayName(member);
    if (displayName) return displayName;
    const memberEmail = getMemberEmail(member);
    if (memberEmail) return memberEmail;
    return member.status === "pending" ? "Invited Member" : "Member";
  };

  const getMemberAvatar = (member) => {
    if (member.uid && memberProfiles[member.uid]) {
      const p = memberProfiles[member.uid];
      return p.photoURL || p.photo || null;
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Manage Members
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Add Member */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Add a Member
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setInviteSent(false);
                setInviteError(null);
              }}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddMember(); }}
              disabled={loading}
            />
            <Button
              variant="contained"
              onClick={handleAddMember}
              disabled={loading || !email.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : <PersonAdd />}
              sx={{ minWidth: "100px", textTransform: "none" }}
            >
              Add
            </Button>
          </Box>

          {/* Invite email sent confirmation */}
          {inviteSent && (
            <Box
              sx={{
                mt: 2, p: 2, borderRadius: "8px",
                backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0",
              }}
            >
              <Typography variant="body2" sx={{ color: "#166534" }}>
                ✓ Invite email sent to <strong>{lastInvitedEmail}</strong>! They'll receive a link to join the project.
              </Typography>
            </Box>
          )}

          {/* Fallback: email send failed — show copy link */}
          {inviteError && (
            <Box
              sx={{
                mt: 2, p: 2, borderRadius: "8px",
                backgroundColor: "var(--bg-elevated)",
                border: "1px solid var(--border-subtle)",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Email send failed{lastInvitedEmail ? ` for ${lastInvitedEmail}` : ""}. Copy this invite link to share manually:
              </Typography>
              <Box
                sx={{
                  display: "flex", alignItems: "center", gap: 1,
                  backgroundColor: "var(--bg-surface)", borderRadius: "6px",
                  padding: "8px 12px", border: "1px solid var(--border-subtle)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "13px",
                  }}
                >
                  {inviteError}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopyLink(inviteError)}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>

        {/* Member List */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Members ({members.length})
          </Typography>
          {members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No members yet. Add someone by email above.
            </Typography>
          ) : (
            <List sx={{ padding: 0 }}>
              {members.map((member, index) => {
                const displayName = getMemberDisplayName(member);
                const memberEmail = getMemberEmail(member);
                const memberName = getMemberName(member);
                const isPending = member.status === "pending";

                return (
                  <ListItem
                    key={member.invite_code || member.uid || index}
                    sx={{
                      borderRadius: "8px",
                      mb: 0.5,
                      "&:hover": { backgroundColor: "var(--bg-hover)" },
                      paddingRight: isPending ? "140px" : "100px",
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={getMemberAvatar(member)}
                        sx={{
                          width: 36,
                          height: 36,
                          backgroundColor: isPending
                            ? theme.palette.grey[400]
                            : theme.palette.primary.main,
                        }}
                      >
                        {(memberName || memberEmail || "U").charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ minWidth: 0, my: 0.5 }}
                      primary={
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {memberName}
                        </Typography>
                      }
                      secondary={
                        <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: "2px", mt: "2px" }}>
                          {memberEmail && displayName && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "12px",
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {memberEmail}
                            </Typography>
                          )}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              fontSize: "11px",
                              textTransform: "capitalize",
                              lineHeight: 1.2,
                            }}
                          >
                            {member.role || "member"}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Chip
                        label={isPending ? "Pending" : "Active"}
                        size="small"
                        sx={{
                          fontSize: "11px",
                          height: "22px",
                          backgroundColor: isPending
                            ? theme.palette.primary.main
                            : "#272727",
                          color: isPending
                            ? "#000"
                            : "#fff",
                        }}
                      />
                      {/* Copy invite link for pending members */}
                      {isPending && member.invite_code && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyLink(
                              `${window.location.origin}/p/${portalId}?invite=${member.invite_code}`
                            )
                          }
                          title="Copy invite link"
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveMember(member)}
                        sx={{ color: theme.palette.primary.main }}
                        title="Remove member"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
